import type {
  AysClinicType,
} from '@bindoc-projects/clinic-domain-lib';
import {
  AYS_CLINIC_TYPE_DISPLAY_NAMES
} from '@bindoc-projects/clinic-domain-lib';
import { BdArrayUtil } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const AYS_CLINIC_TYPE_CONDITION_ID = 'clinicType';
export const FilterConditionClinicTypesPluginType: FilterConditionPluginSpecification<
  AysClinicType[],
  FilterConditionClinicTypesDto,
  AysClinicTypeCondition
> = {
  type: 'clinicTypes',
  category: 'clinic',
  displayName: { de: 'Trägerart' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionClinicTypesDto,
  ): FilterConditionPluginImpl<AysClinicType[]> {
    return new FilterConditionClinicTypesPlugin(obj.clinicTypes as AysClinicType[]);
  },
  fromCondition(
    jsonObj: AysClinicTypeCondition,
  ): FilterConditionPluginImpl<AysClinicType[]> {
    return new FilterConditionClinicTypesPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<AysClinicType[]> {
    return new FilterConditionClinicTypesPlugin([]);
  },
};

export class FilterConditionClinicTypesPlugin extends FilterConditionPluginImpl<
  AysClinicType[],
  FilterConditionClinicTypesDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    AysClinicType[],
    FilterConditionClinicTypesDto,
    AysClinicTypeCondition
  > = FilterConditionClinicTypesPluginType;

  constructor(private clinicTypes: AysClinicType[]) {
    super(FilterConditionClinicTypesPluginType.type);
  }

  clone(): FilterConditionClinicTypesPlugin {
    return new FilterConditionClinicTypesPlugin([...this.clinicTypes]);
  }

  override equals(other: FilterConditionClinicTypesPlugin): boolean {
    return (
      super.equals(other) &&
      BdArrayUtil.equals(this.clinicTypes, other.clinicTypes)
    );
  }

  override validationToText(): string {
    if (this.clinicTypes.length === 0) {
      return 'Mindestens eine Trägerart auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new AysClinicTypeCondition(this.clinicTypes, []);
  }

  toJSON(): FilterConditionClinicTypesDto | null {
    return {
      type: this.type,
      clinicTypes: this.clinicTypes,
    };
  }

  toText(): string | null {
    return this.clinicTypes.length === 1
      ? 'eine Trägerart'
      : `${this.clinicTypes.length} Trägerarten`;
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = this.clinicTypes.map(
      (type) => AYS_CLINIC_TYPE_DISPLAY_NAMES[type] ?? type,
    );
    return { title, items };
  }

  toValue(): AysClinicType[] {
    return this.clinicTypes;
  }
}

export class AysClinicTypeCondition extends BdContentCondition<AysClinicType> {
  constructor(includes: AysClinicType[], excludes: AysClinicType[]) {
    super(AYS_CLINIC_TYPE_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionClinicTypesDto {
  type: string;
  clinicTypes: string[];
}
